import { CONDITION_BASE_URL } from "@qendo/medical";
import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack } from "expo-router";
import { useMemo, useState } from "react";

import { Button } from "~/components/Button";
import { FilteredList } from "~/components/layout/FilteredList";
import { ListingItem } from "~/components/layout/ListingItem";
import { Page } from "~/components/layout/Page";
import { SectionHeader } from "~/components/layout/SectionHeader";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Only } from "~/components/permission/Only";
import { ShareButton } from "~/components/ShareButton";
import type { Condition } from "~/entities/medical/condition";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

export interface ConditionListingItem {
    id: string;
    title: string;
    primary: boolean;
    confirmed: boolean;
}

const type = MedicalType.condition;

export default function ConditionsIndex() {
    const { listMedical, refreshMedicalCache } = useMedical();
    const { findOption } = useOptions();
    const [refreshing, setRefreshing] = useState(false);

    const refresh = async () => {
        if (refreshing) {
            return;
        }

        setRefreshing(true);
        await refreshMedicalCache(type).finally(() => setRefreshing(false));
    };

    const items: ConditionListingItem[] = useMemo(() => {
        return listMedical(type).map((c: Condition) => ({
            id: c.id as string,
            title: findOption(OptionType.conditions, c.option_id)?.title as string,
            primary: c.primary,
            confirmed: c.confirmed,
        }));
    }, [listMedical, findOption]);

    const has_items = items.length > 0;

    return (
        <Authorize ability="view" section="conditions">
            <Stack.Screen
                options={{
                    headerRight: () => <ShareButton title="Share conditions" initial={["conditions", "photos"]} />,
                }}
            />
            <Page spacer refreshWith={refresh} isRefreshing={refreshing} scrollable={!has_items}>
                <SectionHeader
                    title="Conditions"
                    icon="heart-plus"
                    button={() => (
                        <Can ability="update" section="conditions">
                            <Button
                                title="Add condition"
                                variant="primary"
                                size="lg"
                                href="/health-profile/conditions/create"
                            />
                        </Can>
                    )}
                >
                    Document diagnosed and suspected conditions to keep them organised and accessible.
                </SectionHeader>
                <Only when={items.length}>
                    <FilteredList<ConditionListingItem>
                        data={items}
                        searchWith={(item, term) => item.title.toLowerCase().includes(term.toLowerCase())}
                        searchPlaceholder="Search by name"
                        refreshWith={refresh}
                        isRefreshing={refreshing}
                        groups={[
                            { name: "Known", matches: (item) => item.confirmed },
                            { name: "Suspected", matches: (item) => !item.confirmed },
                        ]}
                        renderItem={({ item }) => (
                            <ListingItem
                                title={item.title}
                                hideChevron
                                badge={item.primary ? "Primary condition" : null}
                                href={{ pathname: `${CONDITION_BASE_URL}/[id]/show`, params: { id: item.id } }}
                                last
                            />
                        )}
                    />
                </Only>
            </Page>
        </Authorize>
    );
}
